// What survives a closed tab: the last picture of each document, the library
// listing, and every operation that has not reached the server yet.
//
// The document is cached as the server last gave it, with unsent operations
// kept separately. Reopening replays those onto whatever the server says now,
// so the same edit is never applied twice and never quietly lost. Caching the
// merged view instead would drift the moment the server had news of its own.

import type { DocumentSummary } from "../api/client";
import { type DocState, type Op, applyOp } from "../store/ops";
import { DOCUMENTS, OUTBOX, allByIndex, get, put, remove, removeMany } from "./db";

export interface CachedDocument {
  doc: DocState;
  rev: number;
  savedAt: number;
}

export interface QueuedOp {
  key: string;
  documentId: string;
  seq: number;
  op: Op;
}

export function loadDocument(id: string): Promise<CachedDocument | null> {
  return get<CachedDocument>(DOCUMENTS, id);
}

export function saveDocument(id: string, doc: DocState, rev: number): Promise<unknown> {
  return put(DOCUMENTS, { doc, rev, savedAt: Date.now() } satisfies CachedDocument, id);
}

export function forgetDocument(id: string): Promise<unknown> {
  return remove(DOCUMENTS, id);
}

export function loadQueue(documentId: string): Promise<QueuedOp[]> {
  return allByIndex<QueuedOp>(OUTBOX, "documentId", documentId).then((rows) =>
    rows.sort((a, b) => a.seq - b.seq),
  );
}

export function enqueue(documentId: string, seq: number, op: Op): Promise<unknown> {
  return put(OUTBOX, { key: `${documentId}:${seq}`, documentId, seq, op } satisfies QueuedOp);
}

/** Replace the operation stored for one sequence number, for the case where
 *  consecutive typing is collapsed into a single operation before sending. */
export function replaceQueued(documentId: string, seq: number, op: Op): Promise<unknown> {
  return enqueue(documentId, seq, op);
}

export function dequeue(documentId: string, seqs: number[]): Promise<unknown> {
  return removeMany(
    OUTBOX,
    seqs.map((seq) => `${documentId}:${seq}`),
  );
}

export const LIBRARY_KEY = "__library__";

export function loadLibrary(): Promise<DocumentSummary[] | null> {
  return get<{ documents: DocumentSummary[] }>(DOCUMENTS, LIBRARY_KEY).then((v) => v?.documents ?? null);
}

export function saveLibrary(documents: DocumentSummary[]): Promise<unknown> {
  return put(DOCUMENTS, { documents }, LIBRARY_KEY);
}

/** The document as this device should show it: the server's version with any
 *  operations that have not reached it yet replayed on top.
 *
 *  An operation that no longer fits is dropped rather than thrown: it means
 *  the server already has it and our reply went missing, so its effect is
 *  already present in what the server returned.
 */
export function replay(base: DocState, ops: Op[]): DocState {
  let doc = base;
  for (const op of ops) {
    try {
      doc = applyOp(doc, op);
    } catch {
      // already reflected in `base`
    }
  }
  return doc;
}
